import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// project import
import useAuth from '../../hooks/useAuth';

// ==============================|| AUTH GUARD ||============================== //

const AuthGuard = ({ children }) => {
  const { isLoggedIn, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  let isAllowed = isLoggedIn;
  let redirectTarget = null;
  let registerStep = null;
  let registerEmail = null;

  if (!isLoggedIn) {
    isAllowed = false;
    redirectTarget = '/login';
  } else {
    // Role-based and permission-based route protection
    try {
      const userInfo = user || JSON.parse(localStorage.getItem('userInfo') || '{}');
      const roleName = userInfo.role_name;
      const isTenantsAdmin =
        roleName === 'TenantsAdmin' ||
        roleName === 'Client Admin' ||
        userInfo.role_id === 2 ||
        userInfo.role_id === '2';
      const isSubscriptionOwner = isTenantsAdmin;

      const hasNoSubscription =
        userInfo.subscription === false ||
        userInfo.subscription === 'false' ||
        userInfo.subscription === null ||
        userInfo.subscription === undefined;

      if (isSubscriptionOwner && hasNoSubscription) {
        if (!location.pathname.includes('/admin/subscription')) {
          isAllowed = false;
          redirectTarget = '/admin/subscription';
        }
      } else if (isTenantsAdmin) {
        if (userInfo.isEmailVerified === false) {
          isAllowed = false;
          redirectTarget = '/auth/register';
          registerStep = 'VERIFY_EMAIL';
          registerEmail = userInfo.email;
        }
      }

      const isSuperAdmin = roleName === 'SuperAdmin';

      if (isAllowed && isSuperAdmin) {
        const { pathname } = location;
        const isAuthorizedPath =
          pathname === '/admin/dashboard' ||
          pathname === '/admin/client-management' ||
          pathname === '/admin/settings' ||
          pathname === '/profile/edit-profile' ||
          pathname === '/admin/my-chapters' ||
          pathname.startsWith('/admin/my-chapters/') ||
          pathname === '/admin/manage-templates' ||
          pathname.startsWith('/admin/manage-templates/') ||
          pathname === '/admin/ibp-reference' ||
          pathname === '/admin/support-tickets' ||
          pathname === '/admin/help-tutorials' ||
          pathname === '/admin/ai-documents' ||
          pathname === '/admin/audit-log';

        if (!isAuthorizedPath) {
          isAllowed = false;
          redirectTarget = '/admin/dashboard';
        }
      }

      // Restrict client-management and audit-log to SuperAdmin only
      if (isAllowed && !isSuperAdmin) {
        const { pathname } = location;
        if (pathname.startsWith('/admin/client-management') || pathname.startsWith('/admin/audit-log')) {
          isAllowed = false;
          redirectTarget = '/admin/dashboard';
        }
      }

      const isDelegate =
        userInfo.role_id === 4 ||
        userInfo.role_id === '4' ||
        (userInfo.role_name === 'DelegateAdmin' || userInfo.role_name === 'DeletegateAdmin') ||
        userInfo.role === 'Delegate Admin';

      if (isAllowed && isDelegate) {
        const { pathname } = location;
        const perms = userInfo.permissions || userInfo;

        console.log('AuthGuard delegate permission check:', {
          pathname,
          userInfo,
          perms,
          hasDashboard: perms.dashboard,
          hasAuditor: perms.manage_auditor
        });

        const hasAnyPermission =
          (perms.dashboard !== null && perms.dashboard !== undefined) ||
          (perms.manage_auditor !== null && perms.manage_auditor !== undefined) ||
          (perms.ibp_ref !== null && perms.ibp_ref !== undefined) ||
          (perms.manage_template !== null && perms.manage_template !== undefined) ||
          (perms.manage_chapter !== null && perms.manage_chapter !== undefined) ||
          (perms.auditor_audit !== null && perms.auditor_audit !== undefined) ||
          (perms.audit_report !== null && perms.audit_report !== undefined) ||
          (perms.manage_subscription !== null && perms.manage_subscription !== undefined) ||
          (perms.manage_custom_role !== null && perms.manage_custom_role !== undefined) ||
          (perms.manage_support_ticket !== null && perms.manage_support_ticket !== undefined) ||
          (perms.my_audit !== null && perms.my_audit !== undefined);

        const getFirstAllowedRoute = () => {
          if (perms.dashboard !== null && perms.dashboard !== undefined) return '/admin/dashboard';
          if (perms.manage_auditor !== null && perms.manage_auditor !== undefined) return '/admin/manage-auditors';
          if (perms.ibp_ref !== null && perms.ibp_ref !== undefined) return '/admin/ibp-reference';
          if (perms.manage_template !== null && perms.manage_template !== undefined) return '/admin/manage-templates';
          if (perms.manage_chapter !== null && perms.manage_chapter !== undefined) return '/admin/my-chapters';
          if (perms.my_audit !== null && perms.my_audit !== undefined) return '/admin/manage-sites';
          if (perms.auditor_audit !== null && perms.auditor_audit !== undefined) return '/admin/manage-sites';
          if (perms.audit_report !== null && perms.audit_report !== undefined) return '/admin/audit-reports';
          if (perms.manage_subscription !== null && perms.manage_subscription !== undefined) return '/admin/subscription';
          if (perms.manage_custom_role !== null && perms.manage_custom_role !== undefined) return '/admin/manage-delegate-role';
          if (perms.manage_support_ticket !== null && perms.manage_support_ticket !== undefined) return '/admin/support-tickets';
          return '/admin/settings';
        };

        let hasAccess = true;

        if (pathname.startsWith('/admin/manage-delegate-role')) {
          hasAccess = perms.manage_custom_role !== null && perms.manage_custom_role !== undefined;
        } else if (pathname === '/admin/dashboard') {
          hasAccess = !hasAnyPermission || (perms.dashboard !== null && perms.dashboard !== undefined);
        } else if (pathname.startsWith('/admin/manage-auditors')) {
          hasAccess = perms.manage_auditor !== null && perms.manage_auditor !== undefined;
        } else if (pathname.startsWith('/admin/ibp-reference')) {
          hasAccess = perms.ibp_ref !== null && perms.ibp_ref !== undefined;
        } else if (pathname.startsWith('/admin/manage-templates')) {
          hasAccess = perms.manage_template !== null && perms.manage_template !== undefined;
        } else if (pathname.startsWith('/admin/my-chapters')) {
          hasAccess = perms.manage_chapter !== null && perms.manage_chapter !== undefined;
        } else if (pathname.startsWith('/admin/manage-sites') || pathname.startsWith('/admin/site/')) {
          hasAccess = 
            (perms.my_audit !== null && perms.my_audit !== undefined) || 
            (perms.auditor_audit !== null && perms.auditor_audit !== undefined);
        } else if (pathname.startsWith('/admin/audit-reports')) {
          hasAccess = perms.audit_report !== null && perms.audit_report !== undefined;
        } else if (pathname.startsWith('/admin/subscription')) {
          hasAccess = perms.manage_subscription !== null && perms.manage_subscription !== undefined;
        } else if (pathname.startsWith('/admin/support-tickets')) {
          hasAccess = perms.manage_support_ticket !== null && perms.manage_support_ticket !== undefined;
        }

        if (!hasAccess) {
          isAllowed = false;
          const route = getFirstAllowedRoute();
          console.log('AuthGuard hasAccess is false, calculating redirect:', {
            pathname,
            firstAllowedRoute: route,
            shouldRedirect: route !== pathname
          });
          if (route !== pathname) {
            redirectTarget = route;
          }
        }
      }
    } catch (err) {
      console.error('Error checking permissions in AuthGuard:', err);
    }
  }

  useEffect(() => {
    if (!isAllowed && redirectTarget) {
      if (redirectTarget === '/login') {
        navigate('/login', {
          state: { from: location.pathname },
          replace: true
        });
      } else if (redirectTarget === '/auth/register') {
        navigate('/auth/register', { state: { step: registerStep, email: registerEmail }, replace: true });
      } else {
        navigate(redirectTarget, { replace: true });
      }
    }
  }, [isAllowed, redirectTarget, registerStep, registerEmail, navigate, location.pathname]);

  if (!isAllowed) {
    return null;
  }

  return children;
};

AuthGuard.propTypes = {
  children: PropTypes.node
};

export default AuthGuard;
